import { z } from 'zod';
import { CODE_PAGE_KEYS } from '../escpos/codepages.js';
import { activationCodeInput } from './auth.js';
import { objectId, optional, requiredText, text } from './common.js';

/**
 * Printer, print-agent and print-job inputs.
 *
 * Paths and names here end up on the outlet PC where the print agent runs as a service, so
 * they are allow-listed tightly (the agent checks again): a serial port or printer device file
 * only, never an arbitrary file; Windows/CUPS printer names are passed as arguments, never
 * through a shell.
 */
export const SERIAL_PATH_RE = /^(?:COM\d{1,3}|\/dev\/(?:tty(?:USB|ACM|S)\d{1,3}|(?:cu|tty)\.[A-Za-z0-9._-]{1,60}))$/;
export const USB_PATH_RE = /^\/dev\/usb\/lp\d{1,2}$/;
export const HOST_RE =
  /^(?:\d{1,3}(?:\.\d{1,3}){3}|[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*)$/;
// Printable characters only: no control characters, quotes or shell metacharacters.
export const PRINTER_NAME_RE = /^[^\p{Cc}"'`$;&|<>]{1,120}$/u;
export const BAUD_RATES = Object.freeze([9600, 19200, 38400, 57600, 115200]);

const station = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?:\*|[a-z0-9 _-]{2,30})$/, '2–30 letters/digits');

export const printerProfileSchema = z.strictObject({
  paperWidth: z.enum(['58mm', '80mm']),
  charsPerLine: z.coerce.number().int().min(24).max(64),
  dotWidth: z.coerce.number().int().min(256).max(832),
  codePage: z.enum(CODE_PAGE_KEYS),
  cut: z.enum(['partial', 'full', 'none']),
  drawer: z.strictObject({ enabled: z.boolean(), pin: z.coerce.number().pipe(z.union([z.literal(2), z.literal(5)])) }),
  density: z.coerce.number().int().min(-6).max(6),
  feedLines: z.coerce.number().int().min(0).max(10),
  copies: z.coerce.number().int().min(1).max(5),
  rasterText: z.boolean(),
  rupeeFallback: z.enum(['Rs.', 'Rs', 'INR', '']),
  qrMode: z.enum(['native', 'raster', 'off']),
  printLogo: z.boolean(),
});

const agentConnection = z
  .strictObject({
    via: z.literal('agent'),
    agentId: objectId,
    kind: z.enum(['network', 'windows', 'serial', 'usb', 'cups']),
    host: optional(z.string().trim().regex(HOST_RE, 'Enter an IP address or host name')),
    // Below 1024 are system services (SSH, HTTP, SMB…), never printers.
    port: optional(z.coerce.number().int().min(1024, 'Use the printer port (usually 9100)').max(65535)),
    printerName: optional(z.string().trim().regex(PRINTER_NAME_RE, 'Invalid printer name')),
    path: optional(z.string().trim()),
    baudRate: optional(
      z.coerce
        .number()
        .int()
        .pipe(z.union(BAUD_RATES.map((b) => z.literal(b)))),
    ),
  })
  .superRefine((c, ctx) => {
    const need = (field, msg) => ctx.addIssue({ code: 'custom', path: [field], message: msg });
    if (c.kind === 'network' && !c.host) need('host', 'Enter the printer IP address');
    if ((c.kind === 'windows' || c.kind === 'cups') && !c.printerName) need('printerName', 'Choose the printer');
    if (c.kind === 'serial' && !SERIAL_PATH_RE.test(c.path ?? ''))
      need('path', 'Use a serial port such as COM3 or /dev/ttyUSB0');
    if (c.kind === 'usb' && !USB_PATH_RE.test(c.path ?? '')) need('path', 'Use a printer device such as /dev/usb/lp0');
  });

const deviceConnection = z.strictObject({
  via: z.literal('device'),
  deviceId: objectId,
  api: z.enum(['webusb', 'webserial', 'webbluetooth']),
  usb: optional(
    z.strictObject({
      vendorId: z.number().int().min(0).max(0xffff),
      productId: z.number().int().min(0).max(0xffff),
      serialNumber: optional(text(80)),
      productName: optional(text(120)),
    }),
  ),
  serial: optional(
    z.strictObject({
      usbVendorId: optional(z.number().int().min(0).max(0xffff)),
      usbProductId: optional(z.number().int().min(0).max(0xffff)),
      baudRate: z.coerce
        .number()
        .int()
        .pipe(z.union(BAUD_RATES.map((b) => z.literal(b)))),
    }),
  ),
  bluetooth: optional(z.strictObject({ id: optional(text(120)), name: optional(text(120)) })),
});

export const printerConnectionSchema = z.discriminatedUnion('via', [agentConnection, deviceConnection]);

export const printerRolesSchema = z.strictObject({
  receipt: z.boolean(),
  token: z.boolean(),
  kotStations: z.array(station).max(20),
});

export const printerSchema = z.strictObject({
  outletId: objectId,
  name: requiredText(60),
  connection: printerConnectionSchema,
  profile: printerProfileSchema,
  roles: printerRolesSchema,
  isActive: z.boolean().default(true),
});

export const printerListQuery = z.strictObject({ outletId: objectId.optional() });

/** Device-local printers report their state with the heartbeat / after each job. */
export const printerStatusSchema = z.strictObject({
  state: z.enum(['online', 'warning', 'error', 'offline', 'unknown']),
  message: optional(text(120)),
});

// ---------------------------------------------------------------- print agents
export const agentCodeSchema = z.strictObject({
  outletId: objectId,
  expiresInHours: z.coerce.number().int().min(1).max(168).default(24),
});

/** Public: the agent exchanges a one-time code for its credential. */
export const agentPairSchema = z.strictObject({
  code: activationCodeInput,
  name: requiredText(60),
  hostname: optional(text(120)),
  platform: optional(text(40)),
  version: optional(text(40)),
});

export const agentUpdateSchema = z.strictObject({ name: requiredText(60) });

// ---------------------------------------------------------------- jobs
export const PRINT_JOB_KINDS = Object.freeze(['receipt', 'kot', 'token', 'credit_note', 'test', 'drawer', 'report']);
export const MAX_PRINT_JOB_BYTES = 512 * 1024;

export const printJobSchema = z.strictObject({
  printerId: objectId,
  kind: z.enum(PRINT_JOB_KINDS),
  ref: optional(
    z.strictObject({
      billId: optional(objectId),
      kotId: optional(objectId),
      creditNoteId: optional(objectId),
      label: optional(text(60)),
    }),
  ),
  // ESC/POS bytes, base64. Rendered by the client so ₹, logos and regional text can be rasters.
  data: z
    .string()
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Invalid print data')
    .max(Math.ceil((MAX_PRINT_JOB_BYTES * 4) / 3) + 4, 'Print job is too large'),
});

export const drawerOpenSchema = z.strictObject({
  printerId: objectId,
  reason: optional(text(120)),
});

export const printJobListQuery = z.strictObject({
  printerId: objectId.optional(),
  status: z.enum(['queued', 'sent', 'printed', 'failed', 'expired']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
